import { injectable } from 'inversify';
import { Brand, Carrier } from '../integration/enums';
import { BrandService } from './brandService';
import { PrivacyInformation, VoipInformation, PlanSelectorProductInformation, DefaultPlanSelectorProducts, FeaturesData, HelpOption, LegalDocuments } from './brandServiceInterface';

/**
 * Brand-specific logic for UNI.
 */
@injectable()
export class NexeeBrandService extends BrandService {
  public getName (): Brand {
    return Brand.NEX;
  }

  public getTitle (): string {
    return 'Nexee';
  }

  public getHeroTitle (): string {
    return 'Here. Now. On.';
  }

  public getHomeAnnouncement (): string {
    return '';
  }

  public getHeroImage (): string {
    return this.getAssetsBaseUrl() + `/${this.getName()}/${this.getName().toLowerCase()}-hero2.png`;
  }

  public getPrivacyInformation (): PrivacyInformation {
    return {
      brand: 'Nexee',
      brandPlural: "Nexee's",
      brandUrl: 'www.nexee.au',
      policyUrl: 'www.nexee.au/legal',
      privacyOfficerDetails: {
        email: 'privacy@nexee.au',
        phone: '1300 463 933',
        businessName: 'Uniti Group Limited',
        address: 'Suite 1, Level 16, 323 Castlereagh Street, Haymarket NSW 2000'
      }
    };
  }

  public getVoipInformation (): VoipInformation {
    return {
      email: 'support@nexee.au',
      phone: '1300 463 933',
      csaAgreementUrl: 'https://www.unitiwireless.com/pdf/uni/Customer%20Service%20Agreement.pdf'
    };
  }

  public getPlanSelectorProductInformation (carrier?:string): PlanSelectorProductInformation {
    if (carrier === Carrier.NB) {
      return {
        standardFeatures: [
          'Unlimited Data'
        ],
        standardLinks: [
          {
            name: 'Critical Information Summary',
            link: this.getAssetsBaseUrl() + `/${this.getName()}/critical-information-summary-internet-nbn.pdf`
          },
          {
            name: 'Key Fact Sheet',
            link: this.getAssetsBaseUrl() + `/${this.getName()}/key-fact-sheet-nbn.pdf`
          }
        ]
      };
    }
    return {
      standardFeatures: [
        'Unlimited Data'
      ],
      standardLinks: [
        {
          name: 'Critical Information Summary',
          link: this.getAssetsBaseUrl() + `/${this.getName()}/critical-information-summary-internet.pdf`
        },
        {
          name: 'Key Fact Sheet',
          link: this.getAssetsBaseUrl() + `/${this.getName()}/key-fact-sheet.pdf`
        }
      ]
    };
  }

  public getDefaultPlanSelectorProducts (): Array<DefaultPlanSelectorProducts> {
    return [{
      id: '',
      name: 'The 25',
      description: 'Perfect for single-person households',
      price: 80,
      typicalEveningSpeed: 24.7,
      download: 25,
      upload: 5,
      img: this.getAssetsBaseUrl() + `/${this.getName()}/the25icon.png`,
      imghover: this.getAssetsBaseUrl() + `/${this.getName()}/the25icon-hover.png`
    }, {
      id: '',
      name: 'The 50',
      description: 'Suitable for 2-3 users doing basic browsing',
      price: 100,
      typicalEveningSpeed: 48.4,
      download: 50,
      upload: 20,
      img: this.getAssetsBaseUrl() + `/${this.getName()}/the50icon.png`,
      imghover: this.getAssetsBaseUrl() + `/${this.getName()}/the50icon-hover.png`
    }, {
      id: '',
      name: 'The 100',
      description: 'Great for smooth online gaming and 4K streaming',
      price: 105,
      typicalEveningSpeed: 96.9,
      download: 100,
      upload: 20,
      img: this.getAssetsBaseUrl() + `/${this.getName()}/the100icon.png`,
      imghover: this.getAssetsBaseUrl() + `/${this.getName()}/the100icon-hover.png`
    }, {
      id: '',
      name: 'The 100+',
      description: 'Suitable for regular working from home',
      price: 110,
      typicalEveningSpeed: 96.9,
      download: 100,
      upload: 40,
      img: this.getAssetsBaseUrl() + `/${this.getName()}/the100plusicon.png`,
      imghover: this.getAssetsBaseUrl() + `/${this.getName()}/the100plusicon-hover.png`
    }, {
      id: '',
      name: 'The 500',
      description: 'Super-fast speeds for households with multiple devices',
      price: 108,
      typicalEveningSpeed: 0,
      download: 500,
      upload: 50,
      img: this.getAssetsBaseUrl() + `/${this.getName()}/the500icon.png`,
      imghover: this.getAssetsBaseUrl() + `/${this.getName()}/the500icon-hover.png`
    }, {
      id: '',
      name: 'The 750',
      description: 'Ultra-smooth 8K streaming and gaming',
      price: 119,
      typicalEveningSpeed: 0,
      download: 750,
      upload: 50,
      img: this.getAssetsBaseUrl() + `/${this.getName()}/the750icon.png`,
      imghover: this.getAssetsBaseUrl() + `/${this.getName()}/the750icon-hover.png`
    }, {
      id: '',
      name: 'The 1000',
      description: 'Suitable for simultaneous video conferences',
      price: 132,
      typicalEveningSpeed: 919,
      download: 1000,
      upload: 100,
      img: this.getAssetsBaseUrl() + `/${this.getName()}/the1000icon.png`,
      imghover: this.getAssetsBaseUrl() + `/${this.getName()}/the1000icon-hover.png`
    }];
  }

  public getFeaturesData (): FeaturesData {
    return {
      header: {
        headline: 'We\'ve got your internet covered!',
        tagline: ''
      },
      features: [{
        name: 'Always Here',
        description: 'Benefit from a seamless, connected experience in your apartment and building\'s common areas.',
        img: this.getAssetsBaseUrl() + `/${this.getName()}/alwayshere-icon.png`,
        imghover: this.getAssetsBaseUrl() + `/${this.getName()}/alwayshere-icon-hover.png`
      }, {
        name: 'Always Ready',
        description: 'Get online straightaway with a pre-installed router in your home. (Only available at selected properties. Router/modems are available for purchase for other addresses.)',
        img: this.getAssetsBaseUrl() + `/${this.getName()}/alwaysready-icon.png`,
        imghover: this.getAssetsBaseUrl() + `/${this.getName()}/alwaysready-icon-hover.png`
      }, {
        name: 'Always Connected',
        description: 'Struggling with your internet? Contact us until 9pm, 7 days a week.',
        img: this.getAssetsBaseUrl() + `/${this.getName()}/alwaysconnected-icon.png`,
        imghover: this.getAssetsBaseUrl() + `/${this.getName()}/alwaysconnected-icon-hover.png`
      }]
    };
  }

  public getSupportPhoneNumber (): string {
    return '1300 4 NEXEE (1300 463 933)';
  }

  public getSalesPhoneNumber (): string {
    return '1300 463 933';
  }

  public getRelocationPhoneNumber (): string {
    return '1300 463 933';
  }

  public getSupportEmail (): string {
    return 'support@nexee.au';
  }

  public getAddress (): string {
    return 'Level 16, 323 Castlereagh Street, Haymarket NSW 2000';
  }

  public getContacts (): Array<HelpOption> {
    return [{
      name: 'CALL US NOW',
      icon: 'PhoneIcon',
      description: `<div style="height:162px;"><strong>Customer Service</strong><br>Monday – Friday: 8.30am – 6pm AEDT<br>Saturday, Sunday & National Public Holidays: Closed<br><br><strong>Technical Support</strong><br>Monday – Friday: 8am – 9pm AEDT<br>Saturday & Sunday: 9am – 9pm AEDT<br>National Public Holidays: Closed</div><br><br><a class="text-white hover:text-grey-200 hover:underline transition duration-250 bg-brand-primary py-2 px-10 rounded-full text-sm hover:cursor-pointer" href="tel:${this.getSalesPhoneNumber()}">CALL US</a> <br><br> <span class="text-sm font-black">${this.getSupportPhoneNumber()}</span>`
    }, {
      name: 'EMAIL US',
      icon: 'MailIcon',
      description: `<div style="height:162px;">Submit your request via our Contact Form or alternatively, email us directly at support@harbourisp.com.au </div><br><br><a class="text-white hover:text-grey-200 hover:underline transition duration-250 bg-brand-primary py-2 px-10 rounded-full text-sm hover:cursor-pointer" href="mailto:${this.getSupportEmail()}">EMAIL US</a><br><br> <span class="text-sm font-black">${this.getSupportEmail()}</span>`
    }];
  }

  public getLegalDocuments (): LegalDocuments {
    return {
      termsOfService: [
        {
          name: 'Customer Service Agreement',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/customer-service-agreement.pdf`
        },
        {
          name: 'Direct Debit Service Request Agreement',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/direct-debit-service-request-agreement.pdf`
        },
        {
          name: 'Service Terms',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/service-terms.pdf`
        }
      ],
      cis: [
        {
          name: 'Opticomm Residential Broadband Critical Information Summary - October 2026',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/critical-information-summary-internet.pdf`
        },
        {
          name: 'NBN Residential Broadband Critical Information Summary - October 2026',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/critical-information-summary-internet-nbn.pdf`
        }
      ],
      policies: [
        {
          name: 'Billing Policy',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/billing-policy.pdf`
        },
        {
          name: 'Financial Hardship Policy',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/financial-hardship-policy.pdf`
        },
        {
          name: 'Financial Hardship Policy Summary',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/financial-hardship-policy-summary.pdf`
        },
        {
          name: 'Privacy Policy',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/privacy-policy.pdf`
        },
        {
          name: 'Updated Privacy Policy (effective 10 December 2026)',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/privacy-policy-december-2026.pdf`
        },
        {
          name: 'Fair Use Policy',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/fair-use-policy.pdf`
        },
        {
          name: 'Complaints Handling Policy',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/complaints-handling-process.pdf`
        },
        {
          name: 'Family, Domestic & Sexual Violence Support',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/family-domestic-sexual-violence-support.pdf`
        }
      ],
      formsAndGuides: [
        {
          name: 'Authorised Representative',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/authorised-representative.pdf`
        }
      ],
      legacy: [
        {
          name: 'Nexee Opticomm CIS Oct25.pdf',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/legacy/nexee-opticomm-cis-october-2025.pdf`
        },
        {
          name: 'Nexee NBN CIS Oct25.pdf',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/legacy/nexee-nbn-cis-october-2025.pdf`
        },
        {
          name: 'HISP Opticomm CIS Feb25.pdf',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/legacy/HISP_Opticomm CIS_Feb25.pdf`
        },
        {
          name: 'HISP Opticomm CIS Oct24.pdf',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/legacy/HISP_Opticomm CIS_Oct24.pdf`
        },
        {
          name: 'HISP Opticomm CIS Apr24.pdf',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/legacy/HISP_Opticomm CIS_Apr.pdf`
        },
        {
          name: 'HISP NBN CIS Feb25.pdf',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/legacy/HISP_NBN CIS_Feb25.pdf`
        },
        {
          name: 'HISP NBN CIS Oct24.pdf',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/legacy/HISP_NBN CIS_Oct24.pdf`
        },
        {
          name: 'HISP NBN CIS Apr24.pdf',
          url: this.getAssetsBaseUrl() + `/${this.getName()}/legacy/HISP_NBN CIS_Apr24.pdf`
        }
      ]
    };
  }
}
